import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useStudio } from "./StudioContext";
import { StudioPage, Panel, SectionLabel, Badge, Loading, resolveDept, isSdk, DEPT_ORDER } from "./StudioShared";
import { Building2, Cpu, Info } from "lucide-react";

export default function Departments() {
  const { strategists } = useStudio();
  const navigate = useNavigate();

  const grouped = useMemo(() => {
    const g = {};
    (strategists || []).forEach((s) => {
      const d = resolveDept(s);
      (g[d.name] = g[d.name] || []).push({ ...s, _deptSource: d.source });
    });
    return g;
  }, [strategists]);

  if (!strategists) return <StudioPage title="Department Explorer" question="How is expertise organized?"><Loading /></StudioPage>;

  const names = Object.keys(grouped).sort((a, b) => {
    const ia = DEPT_ORDER.indexOf(a), ib = DEPT_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const generatedCount = strategists.filter((s) => resolveDept(s).source === "generated").length;

  return (
    <StudioPage title="Department Explorer" question="How is expertise organized?" testid="departments-explorer">
      <div className="flex items-start gap-2 text-xs text-slate-500 mb-5 bg-navy-800 border border-white/10 rounded-sm p-3" data-testid="departments-note">
        <Info size={14} className="text-gold shrink-0 mt-0.5" />
        {generatedCount === 0
          ? <span>All {strategists.length} strategists are SDK-defined — departments come directly from each strategist's AIPOS SDK spec. No auto-classification needed.</span>
          : <span>Departments come from the AIPOS SDK where defined. {generatedCount} legacy strategist{generatedCount === 1 ? "" : "s"} are auto-classified (marked <span className="text-slate-400">auto</span>) until they migrate to the SDK — no metadata is fabricated.</span>}
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 items-start" data-testid="departments-grid">
        {names.map((name) => (
          <Panel key={name} className="p-4" data-testid={`department-${name.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/5">
              <div className="flex items-center gap-2"><Building2 size={16} className="text-gold" /><span className="text-white font-medium text-sm">{name}</span></div>
              <span className="font-mono text-xs text-slate-500">{grouped[name].length}</span>
            </div>
            <div className="space-y-1.5">
              {grouped[name].map((s) => (
                <button key={s.id} onClick={() => navigate(`/admin/studio/strategists?focus=${s.id}`)} data-testid={`dept-strategist-${s.id}`}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-white/5 transition-colors group">
                  <Cpu size={13} className="text-slate-500 group-hover:text-gold shrink-0" />
                  <span className="text-slate-300 text-sm flex-1 truncate">{s.name}</span>
                  {isSdk(s) ? <Badge tone="gold">SDK</Badge> : s._deptSource === "generated" ? <span className="text-[9px] uppercase tracking-wider text-slate-600">auto</span> : null}
                </button>
              ))}
            </div>
          </Panel>
        ))}
      </div>
    </StudioPage>
  );
}
